"use client";

import { spanishChromeLabel, translationSourceHash } from "@repo/questionnaire";

import dynamic from "next/dynamic";
import { QuestionnaireBasicControl } from "./questionnaire-basic-control";
import { QuestionnaireChoicesControl } from "./questionnaire-choices-control";
import { cn } from "@/lib/utils";
import type { QuestionnaireFieldRendererProps } from "./questionnaire-field-renderer-types";
export type { QuestionnaireFieldRendererProps } from "./questionnaire-field-renderer-types";
const QuestionnaireContactControl = dynamic(() =>
  import("./questionnaire-contact-control").then(
    (m) => m.QuestionnaireContactControl,
  ),
);
const QuestionnaireRepeatableControl = dynamic(() =>
  import("./questionnaire-repeatable-control").then(
    (m) => m.QuestionnaireRepeatableControl,
  ),
);
const QuestionnaireFileControl = dynamic(() =>
  import("./questionnaire-file-control").then(
    (m) => m.QuestionnaireFileControl,
  ),
);
export function QuestionnaireFieldRenderer(
  props: QuestionnaireFieldRendererProps,
) {
  const {
    field,
    error,
    hideLabel = false,
    translations = {},
    locale = "en",
  } = props;
  const translate = (value: string) =>
    locale === "en"
      ? value
      : (translations[translationSourceHash(value)] ??
        (locale === "es" ? spanishChromeLabel(value) : value));

  return (
    <fieldset
      className="flex min-w-0 flex-col gap-3 border-0 p-0"
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${field.id}-error` : undefined}
    >
      {field.kind !== "computed_display" && (
        <legend
          id={`${field.id}-label`}
          className={cn("text-base font-medium", hideLabel && "sr-only")}
        >
          {translate(field.label)}
        </legend>
      )}
      {field.description && !props.hideDescription && (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {translate(field.description)}
        </p>
      )}
      <FieldControl {...props} translations={translations} locale={locale} />
      {error && (
        <p
          id={`${field.id}-error`}
          role="alert"
          className="text-sm text-destructive"
        >
          {translate(error)}
        </p>
      )}
    </fieldset>
  );
}

function FieldControl(props: QuestionnaireFieldRendererProps) {
  const { field } = props;
  if (
    ["page", "text", "date", "long_text", "duration", "business_type"].includes(
      field.kind,
    )
  )
    return <QuestionnaireBasicControl {...props} />;
  if (["enum", "multi_select", "yes_no", "consent"].includes(field.kind))
    return <QuestionnaireChoicesControl {...props} />;
  if (field.kind === "file") return <QuestionnaireFileControl {...props} />;
  if (field.kind === "repeatable_group")
    return <QuestionnaireRepeatableControl {...props} />;
  if (field.kind === "computed_display")
    return props.renderComputed?.(field) ?? null;
  return <QuestionnaireContactControl {...props} />;
}
